/**
 * Consolidated ProtectedRoute Component
 * Single source of truth for route protection across the platform
 * 
 * Features:
 * - Authentication check
 * - Persona-based access control
 * - Role-based access control (admin, moderator)
 * - Onboarding requirement check
 * - Verification requirement check
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2 } from 'lucide-react';
import type { UserPersona } from '@/types/domain/user';

type AppRole = 'super_admin' | 'admin' | 'moderator' | 'user';

interface ProtectedRouteProps {
  children: React.ReactNode;
  
  /** Require user to be authenticated (default: true) */
  requireAuth?: boolean;
  
  /** List of personas allowed to access this route */
  allowedPersonas?: UserPersona[];
  
  /** List of roles allowed to access this route */
  allowedRoles?: AppRole[];
  
  /** Require onboarding to be completed */
  requireOnboarding?: boolean;
  
  /** Require CNIC verification */
  requireVerification?: boolean;
  
  /** Custom fallback path for unauthorized users */
  fallbackPath?: string;
  
  /** Custom fallback component instead of redirect */
  fallback?: React.ReactNode;

  /** Skip the persona-selection redirect (use for admin-only routes where persona is irrelevant) */
  skipPersonaCheck?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  allowedPersonas = [],
  allowedRoles = [],
  requireOnboarding = false,
  requireVerification = false,
  fallbackPath = '/login',
  fallback,
  skipPersonaCheck = false,
}) => {
  const { isAuthenticated, isLoading, persona, user } = useUser();
  const { isAdmin: isAdminServer, isSuperAdmin: isSuperAdminServer, isLoading: isAdminLoading } = useAdminCheck();
  const location = useLocation();

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Check persona selection — but only redirect if the user profile has fully loaded.
  // If authenticated but user object is still null, profile is still loading (safety timer edge case).
  if (!isLoading && requireAuth && isAuthenticated && user && !persona && !skipPersonaCheck) {
    return <Navigate to="/persona-selection" state={{ from: location }} replace />;
  }

  // Still loading profile — show spinner instead of redirecting
  if (requireAuth && isAuthenticated && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Check onboarding completion
  if (requireOnboarding && !user?.onboardingCompleted) {
    // Allow access to the dashboard for onboarding flow
    if (!location.pathname.includes('/dashboard')) {
      return <Navigate to="/dashboard" state={{ from: location }} replace />;
    }
  }

  // Check CNIC verification — server-verified flag on the user profile.
  // Unverified users are redirected to the profile page with ?verify=cnic so the
  // existing CNICUploadModal opens. Preserves origin in location state.
  if (requireVerification && user && !user.cnicVerified && !location.pathname.startsWith('/dashboard/profile')) {
    return (
      <Navigate
        to="/dashboard/profile?verify=cnic"
        state={{ from: location, reason: 'cnic_verification_required' }}
        replace
      />
    );
  }

  // Check persona permissions
  if (allowedPersonas.length > 0 && persona) {
    const isPersonaAllowed = allowedPersonas.includes(persona);
    // Server-verified admin overrides persona checks
    if (!isPersonaAllowed && !isAdminServer) {
      if (fallback) {
        return <>{fallback}</>;
      }
      
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Alert className="max-w-md">
            <Shield className="h-4 w-4" />
            <AlertDescription>
              You don't have permission to access this page. 
              Required access: {allowedPersonas.join(', ')}
            </AlertDescription>
          </Alert>
        </div>
      );
    }
  }

  // Check role permissions — server-verified via has_role() RPC, never trust client-side user.role
  if (allowedRoles.length > 0) {
    // Wait for the admin check to resolve before deciding
    if (isAdminLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Verifying access...</p>
          </div>
        </div>
      );
    }

    const wantsAdmin = allowedRoles.includes('admin') || allowedRoles.includes('super_admin');
    const hasRequiredRole = (wantsAdmin && (isAdminServer || isSuperAdminServer));

    if (!hasRequiredRole && !isSuperAdminServer) {
      if (fallback) {
        return <>{fallback}</>;
      }
      
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Alert className="max-w-md">
            <Shield className="h-4 w-4" />
            <AlertDescription>
              You don't have the required role to access this page.
              Required roles: {allowedRoles.join(', ')}
            </AlertDescription>
          </Alert>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;

// Convenience components for common use cases

export const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute requireAuth>{children}</ProtectedRoute>
);

export const AdminRoute: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <ProtectedRoute 
    requireAuth 
    allowedRoles={['admin', 'super_admin']} 
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const EmployerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute 
    requireAuth 
    allowedPersonas={['individual-employer', 'business-employer']}
  >
    {children}
  </ProtectedRoute>
);

export const ProviderRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute 
    requireAuth 
    allowedPersonas={['provider', 'agency']}
  >
    {children}
  </ProtectedRoute>
);

export const JobSeekerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute 
    requireAuth 
    allowedPersonas={['job-seeker']}
  >
    {children}
  </ProtectedRoute>
);
